import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useApp } from "../contexts/AppContext";
import api, { formatApiError } from "../lib/api";
import { Search, Clock, CheckCircle2, XCircle, Loader2, Package } from "lucide-react";

const STATUS_META = {
    "Received": { icon: Clock, color: "text-blue-500", bg: "bg-blue-500/10" },
    "In Progress": { icon: Loader2, color: "text-amber-500", bg: "bg-amber-500/10" },
    "Approved": { icon: CheckCircle2, color: "text-emerald-500", bg: "bg-emerald-500/10" },
    "Ready for Pickup": { icon: Package, color: "text-purple-500", bg: "bg-purple-500/10" },
    "Completed": { icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-500/10" },
    "Rejected": { icon: XCircle, color: "text-red-500", bg: "bg-red-500/10" },
};

const Track = () => {
    const { t } = useApp();
    const [params] = useSearchParams();
    const [ref, setRef] = useState(params.get("ref") || "");
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    const doTrack = async (r) => {
        const q = (r || ref).trim().toUpperCase();
        if (!q) return;
        setLoading(true); setErr(""); setResult(null);
        try {
            const { data } = await api.get(`/bookings/track/${q}`);
            setResult(data);
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || t.track.notFound);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (params.get("ref")) doTrack(params.get("ref"));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div data-testid="page-track" className="container-x section-pad max-w-3xl">
            <div className="chip bg-[hsl(var(--royal))]/10 text-[hsl(var(--royal))] mb-4">{t.nav.track}</div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">{t.track.title}</h1>
            <p className="mt-3 text-muted-foreground">{t.track.desc}</p>

            <form onSubmit={(e) => { e.preventDefault(); doTrack(); }} className="mt-8 flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                    <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input data-testid="track-input" value={ref} onChange={(e) => setRef(e.target.value)} placeholder={t.track.placeholder}
                        className="w-full h-14 pl-11 pr-4 rounded-full border-2 border-border bg-background focus:border-[hsl(var(--royal))] focus:outline-none font-mono uppercase tracking-wider" />
                </div>
                <button data-testid="track-btn" type="submit" className="btn-royal !py-3 !px-8" disabled={loading}>
                    {loading ? "…" : t.track.track}
                </button>
            </form>

            {err && <div data-testid="track-error" className="mt-6 rounded-xl border border-destructive/40 bg-destructive/10 text-destructive p-4 text-sm">{err}</div>}

            {result && (() => {
                const meta = STATUS_META[result.status] || STATUS_META["Received"];
                const Icon = meta.icon;
                return (
                    <div data-testid="track-result" className="mt-8 rounded-3xl border border-border bg-card p-6 sm:p-8 fade-up">
                        <div className="flex items-start justify-between flex-wrap gap-4">
                            <div>
                                <div className="text-xs uppercase text-muted-foreground tracking-wide">{t.admin.reference}</div>
                                <div className="font-mono font-bold text-xl text-[hsl(var(--royal))]">{result.reference}</div>
                            </div>
                            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${meta.bg} ${meta.color}`}>
                                <Icon size={16} className={result.status === "In Progress" ? "animate-spin" : ""} />
                                {result.status}
                            </div>
                        </div>
                        <div className="mt-6 grid sm:grid-cols-2 gap-4 text-sm">
                            <Info label={t.admin.service} value={result.service_name} />
                            <Info label={t.admin.customer} value={result.full_name} />
                            <Info label={t.admin.phone} value={result.phone} />
                            <Info label={t.admin.date} value={new Date(result.created_at).toLocaleString()} />
                            {result.admin_note && <Info label="Note" value={result.admin_note} className="sm:col-span-2" />}
                        </div>
                    </div>
                );
            })()}
        </div>
    );
};

const Info = ({ label, value, className = "" }) => (
    <div className={`rounded-xl bg-muted/40 border border-border p-3 ${className}`}>
        <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="mt-0.5 font-medium">{value}</div>
    </div>
);

export default Track;
